import { loadAvatarImage, getImageSize } from './imageUtils'
import { buildFaceLayer } from './buildFaceLayer'
import { buildEyesLayer } from './buildEyesLayer'
import { buildIrisLayer } from './buildIrisLayer'
import { buildPupilLayer } from './buildPupilLayer'
import { buildEarsLayer } from './buildEarsLayer'
import { buildMeshContoursLayer } from './buildMeshContoursLayer'
import { buildHairLayer } from './buildHairLayer'
import { extractLandmarkFeatures } from './extractLandmarkFeatures'

export const analyzeAvatarInspector = async ({
  avatarSrc,
  faceLandmarkerImage = null,
  segmenterImage = null,
  hairSegmenterImage = null,
  hairEnhance = false,
  hairEdgeQuality = 'smooth',
  hairMaskSampling = 'bilinear',
  backgroundThreshold = 0.5,
  edgeFeather = 2,
  foregroundDilate = 0,
  extractBackground = true,
  extractForeground = true
}) => {
  if (!avatarSrc || typeof avatarSrc !== 'string') return null
  const image = await loadAvatarImage(avatarSrc)
  const { width, height } = getImageSize(image)

  let landmarks = null
  if (faceLandmarkerImage) {
    try {
      const result = faceLandmarkerImage.detect(image)
      landmarks = result?.faceLandmarks?.[0] || null
    } catch {
      landmarks = null
    }
  }

  const layers = {
    background: null,
    foreground: null,
    face: null,
    hair: null,
    eyes: null,
    irises: null,
    pupils: null,
    ears: null,
    meshContours: null
  }
  let featureMap = null

  if (landmarks?.length) {
    featureMap = extractLandmarkFeatures({ landmarks, width, height })
    layers.face = buildFaceLayer({ landmarks, width, height, features: featureMap })
    layers.eyes = buildEyesLayer({ landmarks, width, height, features: featureMap })
    layers.irises = buildIrisLayer({ landmarks, width, height, features: featureMap })
    layers.pupils = buildPupilLayer({ landmarks, width, height, features: featureMap })
    layers.ears = buildEarsLayer({ landmarks, width, height, features: featureMap })
    layers.meshContours = buildMeshContoursLayer({
      landmarks,
      width,
      height,
      features: featureMap
    })
  }

  if (hairSegmenterImage) {
    try {
      layers.hair = buildHairLayer({
        hairSegmenterImage,
        image,
        width,
        height,
        hairEnhance,
        hairEdgeQuality,
        hairMaskSampling
      })
    } catch {
      layers.hair = null
    }
  }

  // Extract background and foreground using MediaPipe segmenter when available,
  // otherwise fall back to simple edge-based heuristic.
  if (extractBackground || extractForeground) {
    try {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      ctx.drawImage(image, 0, 0, width, height)
      const imageData = ctx.getImageData(0, 0, width, height)
      const { data } = imageData
      const mask = new Uint8Array(width * height)

      if (segmenterImage && typeof segmenterImage.segment === 'function') {
        // Prefer MediaPipe Image Segmenter when available
        try {
          const seg = segmenterImage.segment(image)
          let filled = false
          const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

          // Confidence masks path
          const confidenceMasks = seg?.confidenceMasks
          if (confidenceMasks?.length) {
            const m = confidenceMasks[0]
            const floats = m?.getAsFloat32Array?.()
            const mw = m?.width || 0
            const mh = m?.height || 0
            if (floats?.length && mw > 0 && mh > 0) {
              const sx = (mw - 1) / Math.max(1, width - 1)
              const sy = (mh - 1) / Math.max(1, height - 1)
              const probThresh = backgroundThreshold // 0..1 threshold for person probability
              for (let y = 0; y < height; y++) {
                const fy = y * sy
                const ry = clamp(Math.round(fy), 0, mh - 1)
                const row = ry * mw
                for (let x = 0; x < width; x++) {
                  const fx = x * sx
                  const rx = clamp(Math.round(fx), 0, mw - 1)
                  const p = floats[row + rx] || 0
                  mask[y * width + x] = p > probThresh ? 255 : 0
                }
              }
              filled = true
            }
          }

          // Category mask fallback
          if (!filled) {
            const cat = seg?.categoryMask?.getAsImageData?.()
            if (cat?.data?.length) {
              const mw = cat.width || width
              const mh = cat.height || height
              const sx = (mw - 1) / Math.max(1, width - 1)
              const sy = (mh - 1) / Math.max(1, height - 1)
              const probThresh = backgroundThreshold * 255 // map to 0..255
              // Assume red channel encodes person probability
              for (let y = 0; y < height; y++) {
                const fy = y * sy
                const ry = clamp(Math.round(fy), 0, mh - 1)
                for (let x = 0; x < width; x++) {
                  const fx = x * sx
                  const rx = clamp(Math.round(fx), 0, mw - 1)
                  const di = (ry * mw + rx) * 4
                  const pr = cat.data[di] || 0
                  mask[y * width + x] = pr > probThresh ? 255 : 0
                }
              }
              filled = true
            }
          }

          if (!filled) {
            // As a last resort, fall back to heuristic below
            segmenterImage = null
          }
        } catch {
          // Fallback to heuristic if segmenter throws
        }
      }

      if (!segmenterImage) {
        // Heuristic fallback: simple edge-color distance based segmentation
        const edgeSamples = []
        const sampleSpacing = Math.max(1, Math.floor(Math.min(width, height) / 20))
        for (let x = 0; x < width; x += sampleSpacing) {
          edgeSamples.push({ r: data[x * 4], g: data[x * 4 + 1], b: data[x * 4 + 2] })
          const bottomIdx = ((height - 1) * width + x) * 4
          edgeSamples.push({ r: data[bottomIdx], g: data[bottomIdx + 1], b: data[bottomIdx + 2] })
        }
        for (let y = 0; y < height; y += sampleSpacing) {
          const leftIdx = y * width * 4
          edgeSamples.push({ r: data[leftIdx], g: data[leftIdx + 1], b: data[leftIdx + 2] })
          const rightIdx = (y * width + (width - 1)) * 4
          edgeSamples.push({ r: data[rightIdx], g: data[rightIdx + 1], b: data[rightIdx + 2] })
        }
        const avgEdgeColor = {
          r: edgeSamples.reduce((sum, s) => sum + s.r, 0) / edgeSamples.length,
          g: edgeSamples.reduce((sum, s) => sum + s.g, 0) / edgeSamples.length,
          b: edgeSamples.reduce((sum, s) => sum + s.b, 0) / edgeSamples.length
        }
        const threshold = backgroundThreshold * 255
        for (let i = 0; i < width * height; i++) {
          const idx = i * 4
          const r = data[idx]
          const g = data[idx + 1]
          const b = data[idx + 2]
          const dr = r - avgEdgeColor.r
          const dg = g - avgEdgeColor.g
          const db = b - avgEdgeColor.b
          const diff = Math.sqrt(dr * dr + dg * dg + db * db)
          mask[i] = diff > threshold ? 255 : 0
        }
      }

      // Apply dilate/erode to foreground mask
      if (foregroundDilate !== 0) {
        const tempMask = new Uint8Array(mask)
        const radius = Math.abs(foregroundDilate)
        const isDilate = foregroundDilate > 0

        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            let maxMin = isDilate ? 0 : 255
            for (let dy = -radius; dy <= radius; dy++) {
              for (let dx = -radius; dx <= radius; dx++) {
                const nx = x + dx
                const ny = y + dy
                if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                  const val = tempMask[ny * width + nx]
                  maxMin = isDilate ? Math.max(maxMin, val) : Math.min(maxMin, val)
                }
              }
            }
            mask[y * width + x] = maxMin
          }
        }
      }

      // Apply edge feathering
      if (edgeFeather > 0) {
        const tempMask = new Uint8Array(mask)
        const radius = Math.ceil(edgeFeather)

        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            let sum = 0
            let count = 0
            for (let dy = -radius; dy <= radius; dy++) {
              for (let dx = -radius; dx <= radius; dx++) {
                const dist = Math.sqrt(dx * dx + dy * dy)
                if (dist <= edgeFeather) {
                  const nx = x + dx
                  const ny = y + dy
                  if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                    sum += tempMask[ny * width + nx]
                    count++
                  }
                }
              }
            }
            mask[y * width + x] = count > 0 ? sum / count : tempMask[y * width + x]
          }
        }
      }

      // Create background layer
      if (extractBackground) {
        const bgCanvas = document.createElement('canvas')
        bgCanvas.width = width
        bgCanvas.height = height
        const bgCtx = bgCanvas.getContext('2d')
        const bgImageData = bgCtx.createImageData(width, height)

        for (let i = 0; i < width * height; i++) {
          const idx = i * 4
          const alpha = 255 - mask[i] // Invert mask for background
          bgImageData.data[idx] = data[idx]
          bgImageData.data[idx + 1] = data[idx + 1]
          bgImageData.data[idx + 2] = data[idx + 2]
          bgImageData.data[idx + 3] = alpha
        }

        bgCtx.putImageData(bgImageData, 0, 0)
        layers.background = bgCanvas.toDataURL('image/png')
      }

      // Create foreground layer
      if (extractForeground) {
        const fgCanvas = document.createElement('canvas')
        fgCanvas.width = width
        fgCanvas.height = height
        const fgCtx = fgCanvas.getContext('2d')
        const fgImageData = fgCtx.createImageData(width, height)

        for (let i = 0; i < width * height; i++) {
          const idx = i * 4
          const alpha = mask[i]
          fgImageData.data[idx] = data[idx]
          fgImageData.data[idx + 1] = data[idx + 1]
          fgImageData.data[idx + 2] = data[idx + 2]
          fgImageData.data[idx + 3] = alpha
        }

        fgCtx.putImageData(fgImageData, 0, 0)
        layers.foreground = fgCanvas.toDataURL('image/png')
      }
    } catch (err) {
      console.warn('[Avatar Inspector] Background/foreground extraction failed:', err)
      layers.background = null
      layers.foreground = null
    }
  }

  const detectedParts = Object.entries(layers)
    .filter(([, value]) => typeof value === 'string' && value.length > 0)
    .map(([key]) => key)

  return {
    baseSrc: avatarSrc,
    width,
    height,
    hasFaceLandmarks: !!landmarks?.length,
    featureMap,
    detectedParts,
    layers
  }
}
